import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Input, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import DataTable from '../../components/DataTable';
import FilterPanel from '../../components/FilterPanel';
import DateRangePicker, { type DateRange } from '../../components/DateRangePicker';
import { auditApi } from '../../api/admin.api';
import type { AuditLogDto, AuditQuery } from '../../types/admin';

const ACTION_COLOR: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DEACTIVATE: 'red',
  RESET_PASSWORD: 'orange',
  LOGIN: 'default',
  LOGOUT: 'default',
};

export default function AuditLog() {
  const [query, setQuery] = useState<AuditQuery>({ take: 200 });

  const auditQuery = useQuery({
    queryKey: ['audit', query],
    queryFn: () => auditApi.list(query),
  });

  const onDateChange = (range: DateRange) => {
    setQuery((q) => ({
      ...q,
      from: range?.[0]?.toISOString(),
      to: range?.[1]?.toISOString(),
    }));
  };

  const columns: ColumnsType<AuditLogDto> = [
    {
      title: 'زمان',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: 'کاربر', dataIndex: 'userEmail', key: 'userEmail' },
    {
      title: 'اقدام',
      dataIndex: 'action',
      key: 'action',
      render: (a: string) => <Tag color={ACTION_COLOR[a] ?? 'default'}>{a}</Tag>,
    },
    { title: 'موجودیت', dataIndex: 'entityType', key: 'entityType' },
    { title: 'شناسه', dataIndex: 'entityId', key: 'entityId', width: 90 },
    { title: 'IP', dataIndex: 'ipAddress', key: 'ipAddress', render: (v: string | null) => v ?? '—' },
  ];

  return (
    <Card
      title={<Typography.Title level={4} style={{ margin: 0 }}>گزارش حسابرسی</Typography.Title>}
    >
      <FilterPanel onReset={() => setQuery({ take: 200 })}>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>نوع موجودیت</Typography.Text>
          <Input
            allowClear
            placeholder="مثلاً User"
            style={{ width: 200 }}
            value={query.entityType}
            onChange={(e) => setQuery((q) => ({ ...q, entityType: e.target.value || undefined }))}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>بازه زمانی</Typography.Text>
          <DateRangePicker onChange={onDateChange} />
        </div>
      </FilterPanel>

      <DataTable<AuditLogDto>
        columns={columns}
        dataSource={auditQuery.data ?? []}
        loading={auditQuery.isLoading}
        expandable={{
          expandedRowRender: (record) => (
            <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', direction: 'ltr' }}>
              {record.detail || '—'}
            </Typography.Paragraph>
          ),
          rowExpandable: (record) => !!record.detail,
        }}
      />
    </Card>
  );
}
